/**
 * EL ÚLTIMO PERFIL CONOCIDO, en disco.
 *
 * Para una sola cosa: que quedarse sin señal no te saque la sesión. El árbitro
 * que recarga la página en una cancha sin datos veía "Ingresar" en pleno
 * partido — los tokens estaban, la cola de eventos también, pero
 * `GET /me/profile` no contestaba y la ruta protegida lo mandaba al login.
 *
 * NO es una puerta: el backend valida cada pedido igual. Es la foto de a quién
 * le pertenece esta sesión, para poder dibujar la pantalla sin red.
 *
 * Dos candados, porque es un dato de una persona guardado en un teléfono que
 * puede ser del club:
 *  - VA ATADA AL TOKEN (`sub`): si el que está logueado ahora no es el dueño de
 *    la foto, la foto no sirve. Sin esto, un `GET /me/profile` que devolvía 502
 *    (el servidor despertando) justo en el login de OTRO usuario lo dejaba
 *    entrar con el nombre, el mail y la liga del anterior.
 *  - VENCE. Una foto de hace días no sostiene ninguna sesión: el que agarra el
 *    teléfono en modo avión no ve los datos del dueño para siempre.
 */
import type { ActiveContext, UserProfile } from "../api/auth";

const CLAVE = "forja.perfil";
/** Lo que dura la foto sin poder confirmarla contra el servidor. */
const VENCE_EN_MS = 24 * 60 * 60 * 1000;

export type PerfilCache = {
  user: UserProfile;
  activeContext: ActiveContext | null;
  hasPlayerFicha?: boolean;
};

type Guardado = PerfilCache & { at: number; sub: string | null };

/**
 * El `sub` (id del usuario) del access token, sin librerías ni validar la firma:
 * acá no se decide ningún permiso, sólo se compara CONTRA QUIÉN es la foto.
 */
export function sujetoDelToken(token: string | null): string | null {
  if (!token) return null;
  try {
    const cuerpo = token.split(".")[1];
    if (!cuerpo) return null;
    const json = atob(cuerpo.replace(/-/g, "+").replace(/_/g, "/"));
    const datos = JSON.parse(json) as { sub?: unknown };
    return typeof datos.sub === "string" ? datos.sub : null;
  } catch {
    return null;
  }
}

export function guardarPerfilCache(p: PerfilCache, sub: string | null): void {
  try {
    const g: Guardado = { ...p, at: Date.now(), sub };
    localStorage.setItem(CLAVE, JSON.stringify(g));
  } catch {
    /* sin almacenamiento (Safari privado): se sigue sin caché */
  }
}

/** La foto, sólo si es DE ESTE usuario y no venció. */
export function leerPerfilCache(sub: string | null): PerfilCache | null {
  try {
    const crudo = localStorage.getItem(CLAVE);
    if (!crudo) return null;
    const g = JSON.parse(crudo) as Guardado;
    if (!g || !g.user || !g.user.id) return null;
    // Sin `sub` en el token (formato raro) no se adivina: se descarta.
    if (!sub || g.sub !== sub) return null;
    if (!g.at || Date.now() - g.at > VENCE_EN_MS) return null;
    return { user: g.user, activeContext: g.activeContext, hasPlayerFicha: g.hasPlayerFicha };
  } catch {
    return null;
  }
}

export function borrarPerfilCache(): void {
  try {
    localStorage.removeItem(CLAVE);
  } catch {
    /* idem */
  }
}
